import Joi from 'joi';
import { compact, uniq } from 'lodash';
import { Moment } from 'moment';
import {
	BeforeInsert,
	BeforeUpdate,
	Column,
	Entity,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { booleanTransformer, GenPartial, momentTransformer } from 'typeormgen';
import Character from '../characters/character.entity';
import CmEpisodicActor from '../cm-episodic-actors/cm-episodic-actor.entity';
import { GENDERS } from '../enums/enums';
import EpisodicActor from '../episodic-actors/episodic-actor.entity';
import EpisodicBase from '../episodic-base.entity';
import EpisodicCharacterPicture from '../episodic-character-pictures/episodic-character-picture.entity';
import EpisodicItem from '../episodic-items/episodic-item.entity';
import EpisodicProp from '../episodic-props/episodic-prop.entity';
import Episodic from '../episodics/episodic.entity';
import { Indexable } from '../indexable';
import { Taggable } from '../taggable';
import { EpisodicCharacterESDoc } from './episodic-character.interfaces';

@Entity('episodic_characters')
export default class EpisodicCharacter
	extends EpisodicBase
	implements Indexable<EpisodicCharacterESDoc>, Taggable
{
	static readonly tableName = 'episodic_characters';

	static readonly skipUpdateColumns = [
		'id',
		'episodic_id',
		'episodicActor',
		'characters',
		'created_by',
		'date_created',
	];

	static readonly sortColumns = ['char_num', 'first_name', 'episodic_actor_id'];

	@Column('int')
	episodic_actor_id: number;

	@Column('varchar')
	description: string;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	last_updated_time: Moment;

	@Column('varchar')
	char_count: string;

	@Column({ type: 'int', transformer: booleanTransformer })
	is_primary: boolean;

	@Column('varchar')
	approx_age: string;

	@Column('int')
	gender: GENDERS;

	@Column('varchar')
	last_name: string;

	@Column('varchar')
	first_name: string;

	@Column('varchar', { name: 'char_num' })
	db_char_num: string;

	@BeforeInsert()
	@BeforeUpdate()
	trimCharNum() {
		if (this.db_char_num) {
			this.db_char_num = this.db_char_num.trim();
		}
	}

	@Column('int')
	series_character_id: number;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	date_created: Moment;

	@Column({ type: 'tinyint', transformer: booleanTransformer })
	char_num_by_prod: boolean;

	@OneToMany((type) => Character, (character) => character.episodicCharacter)
	characters: Character[];

	@ManyToOne((type) => Episodic, (episodic) => episodic.episodicCharacters)
	@JoinColumn({
		name: 'episodic_id',
	})
	episodic: Episodic;

	@OneToMany(
		(type) => EpisodicItem,
		(episodicItem) => episodicItem.episodicCharacter
	)
	episodicItems: EpisodicItem[];

	@OneToMany(
		(type) => EpisodicProp,
		(episodicProp) => episodicProp.episodicCharacter
	)
	episodicProps: EpisodicProp[];

	@ManyToOne(
		(type) => EpisodicActor,
		(episodicActor) => episodicActor.episodicCharacters
	)
	@JoinColumn({
		name: 'episodic_actor_id',
	})
	episodicActor: EpisodicActor;
	@JoinColumn({
		name: 'episodic_actor_id',
	})
	cmEpisodicActor: CmEpisodicActor;

	@OneToMany(
		(type) => EpisodicCharacterPicture,
		(episodicCharacterPicture) => episodicCharacterPicture.episodicCharacter
	)
	episodicCharacterPictures: EpisodicCharacterPicture[];

	char_name: string;
	actor_name: string;
	changes;
	static rules() {
		return {
			...super.rules(),
			approx_age: Joi.string().max(100).allow(null, ''),
			char_count: Joi.string().max(500).allow(null, ''),
			char_num_by_prod: Joi.boolean().allow(null).allow(0).allow(1),
			char_num: Joi.string().max(255).allow(null, ''),
			date_created: Joi.date().allow(null),
			description: Joi.string().max(1000).allow(null, ''),
			episodic_actor_id: Joi.number().integer().allow(null),
			first_name: Joi.string().max(100).allow(null, ''),
			gender: Joi.number().integer().allow(null),
			is_primary: Joi.boolean(),
			last_name: Joi.string().max(100).allow(null, ''),
			last_updated_time: Joi.date().allow(null),
			last_updated_user: Joi.number().integer().allow(null),
			series_character_id: Joi.number().integer().allow(null),
		};
	}

	toJSON() {
		return {
			...super.toJSON(),
			approx_age: this.approx_age,
			char_count: this.char_count,
			char_num_by_prod: this.char_num_by_prod,
			char_num: this.char_num,
			date_created: this.date_created,
			description: this.description,
			episodic_actor_id: this.episodic_actor_id,
			first_name: this.first_name,
			gender: this.gender,
			is_primary: this.is_primary,
			last_name: this.last_name,
			last_updated_time: this.last_updated_time,
			series_character_id: this.series_character_id,
		};
	}

	toESDoc(data?: { prodIds: number[] }) {
		let prod_ids = [];
		if (data) {
			const { prodIds } = data;

			if (prodIds) {
				prod_ids = prodIds;
			}
		}
		return {
			id: this.id,
			episodic_id: this.episodic_id,
			first_name: this.first_name,
			last_name: this.last_name,
			is_primary: this.is_primary ? 1 : 0,
			prod_ids,
			char_num: this.char_num,
		};
	}

	assign(props?: GenPartial<EpisodicCharacter>) {
		super.assign(props);

		if (props) {
			if (typeof props.is_primary !== 'undefined') {
				this.is_primary = booleanTransformer.from(props.is_primary);
			}
			if (typeof props.last_updated_time !== 'undefined') {
				this.last_updated_time = momentTransformer.from(
					props.last_updated_time
				);
			}
			if (typeof props.date_created !== 'undefined') {
				this.date_created = momentTransformer.from(props.date_created);
			}
		}
	}

	constructor(props?: GenPartial<EpisodicCharacter>) {
		super(props);
	}

	getUniqueCharNum(characters?: Character[]): String {
		const uniqCharNums = uniq(characters.map(({ char_num }) => char_num));
		if (uniqCharNums.length > 1) {
			return 'MULTI';
		}
		return uniqCharNums[0];
	}

	getCharNum(characters?: Character[]): String {
		if (this.char_num_by_prod && characters) {
			return this.getUniqueCharNum(characters);
		}
		return this.char_num || 'TBD';
	}

	getCharNumOrEmptyString(characters?: Character[]): String | null {
		if (this.char_num_by_prod && characters) {
			return this.getUniqueCharNum(characters);
		}
		return this.char_num || '';
	}

	getFullName(charArr?: Character[], withCharCount: boolean = true) {
		if (this.is_primary) {
			const fullName = compact([this.first_name, this.last_name]).join(' ');
			return `(${this.getCharNum(charArr)}) ${fullName}`.trim();
		} else {
			return `${this.first_name}${
				this.char_count && withCharCount ? ` (${this.char_count})` : ''
			}`;
		}
	}

	getFirstNameWithCharNum(
		charArr?: Character[],
		withCharCount: boolean = true
	) {
		if (this.is_primary) {
			return `(${this.getCharNum(charArr)}) ${this.first_name}`.trim();
		} else {
			return `${this.first_name}${
				this.char_count && withCharCount ? ` (${this.char_count})` : ''
			}`;
		}
	}

	getName() {
		return compact([this.first_name, this.last_name]).join(' ').trim();
	}

	/**
	 * Gets the character's number (if exists) or truncated first name
	 * @returns The character's number or truncated first name, uppercased
	 */
	getCharNumOrAbbrev() {
		if (this.db_char_num && Number(this.db_char_num) !== 0) {
			return this.db_char_num;
		} else if (this.first_name) {
			return this.first_name.substr(0, 3).toUpperCase();
		} else {
			return '';
		}
	}

	getTagName(chars: Character[]) {
		return this.getFullName(chars);
	}

	getDisplayName(charArr: Character[]) {
		return this.getFullName(charArr);
	}

	getShortName() {
		let shortName = this.first_name;
		if (this.last_name && this.last_name.length) {
			shortName += ` ${this.last_name}`;
		}
		return shortName;
	}

	getBudgetCharName() {
		if (!this.is_primary && this.char_count) {
			return `${this.getShortName()} (${this.char_count})`;
		} else {
			return this.getShortName();
		}
	}

	isTBD() {
		return this.char_num === '' || this.char_num === null;
	}
	isMulti() {
		return this.is_primary && this.char_num_by_prod;
	}

	getCharNumRank(ignoreMulti: boolean = false) {
		return !this.is_primary
			? 1
			: this.isTBD()
				? 777
				: this.isMulti() && !ignoreMulti
					? 888
					: 999;
	}

	get char_num() {
		if (this.is_primary) {
			return this.char_num_by_prod ? 'MULTI' : this.db_char_num;
		}
		return '';
	}

	set char_num(charNum: string) {
		this.db_char_num = charNum;
	}
}
